import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { Home, Search, History, LogOut, Moon, Sun, Settings, Heart, Menu, X } from 'lucide-react';

const Navigation = ({ activeTab, setActiveTab }) => {
    const { user, logout } = useAuth();
    const { isDarkMode, toggleTheme } = useTheme();
    const { t } = useLanguage();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navItems = [
        { id: 'dashboard', label: t('navigation.home'), icon: Home },
        { id: 'recommend', label: t('navigation.recommend'), icon: Search },
        { id: 'favorites', label: t('navigation.favorites'), icon: Heart },
        { id: 'history', label: t('navigation.history'), icon: History },
        { id: 'settings', label: t('navigation.settings'), icon: Settings }
    ];

    const handleLogout = () => {
        logout();
    };

    const handleNavClick = (tabId) => {
        setActiveTab(tabId);
        setIsMobileMenuOpen(false); // Mobil menüyü kapat
    };

    return (
        <>
            {/* Compact Desktop/Mobile Header */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 shadow-lg backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex justify-between items-center h-16 sm:h-20 md:h-24 lg:h-28">
                        {/* Logo */}
                        <button
                            onClick={() => handleNavClick('dashboard')}
                            className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer group"
                            aria-label={t('navigation.goToHome')}
                        >
                            <img
                                src="/logo.png"
                                alt="Removvie Logo"
                                className="h-14 sm:h-16 md:h-20 lg:h-24 w-auto transform group-hover:scale-105 transition-transform"
                            />
                        </button>

                        {/* Desktop Navigation - Compact Icons */}
                        <div className="hidden md:flex items-center space-x-1">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleNavClick(item.id)}
                                        className={`p-2 rounded-lg transition-all group relative ${activeTab === item.id
                                            ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300'
                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                            }`}
                                        title={item.label}
                                    >
                                        <Icon className="h-5 w-5" />
                                        {/* Tooltip */}
                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                                            {item.label}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Right Side - Theme + User + Mobile Menu */}
                        <div className="flex items-center space-x-2">
                            {/* Language Selector */}
                            <LanguageSelector />

                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                                title={isDarkMode ? t('navigation.lightTheme') : t('navigation.darkTheme')}
                            >
                                {isDarkMode ? <Sun className="h-4 w-4 sm:h-5 sm:w-5" /> : <Moon className="h-4 w-4 sm:h-5 sm:w-5" />}
                            </button>

                            {/* User Info - Desktop Only */}
                            <div className="hidden lg:flex items-center space-x-2">
                                <div className="text-right">
                                    <p className="text-xs font-medium text-gray-900 dark:text-white">{user?.name}</p>
                                </div>
                            </div>

                            {/* Logout */}
                            <button
                                onClick={handleLogout}
                                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                                title={t('navigation.logout')}
                            >
                                <LogOut className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="md:hidden p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                            >
                                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Dropdown Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden fixed top-16 sm:top-20 md:top-24 lg:top-28 left-0 right-0 z-40 bg-white dark:bg-gray-800 shadow-lg border-t border-gray-200 dark:border-gray-700">
                    <div className="px-4 py-3 space-y-1">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleNavClick(item.id)}
                                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all ${activeTab === item.id
                                        ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span className="font-medium">{item.label}</span>
                                </button>
                            );
                        })}

                        {/* User Info in Mobile Menu */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                            <div className="px-4 py-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black bg-opacity-25 z-30"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}
        </>
    );
};

export default Navigation; 